import React from 'react';
import moment from 'moment';
import { Col, Row } from 'react-grid-system';
import Icon from '@Components/Icon/Icon';
import Divider from '@Components/Divider/Divider';
import Accordion from '@Components/Accordion/Accordion';
import { ParagraphM } from '@Components/Typography/Typography.styled';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import useTranslate from '@Utils/hooks/useTranslate';
import { isSoonPeorderDate } from '@Utils/date/moment';
import { useOutlet } from '@Utils/context/OutletContext';
import MapDetails from './MapDetails';
import LabeledInformation from './LabeledInformation';
import {
	AccordionContentWrapper,
	Data,
	DeliveryMethodDescription,
	DeliveryMethodTitle,
	DeliveryMethodWrapper,
	DescriptionWrapper,
	Label,
	LabeledInformationWrapper,
	SectionHeader,
	AppointmentSectionHeader,
	DaysWrapper,
	AppointmentDetailsWrapper,
} from '@Components/Card/cards/ShippingDetails/ShippingDetails.styled';
import { IAddress } from '@ApiModels/commerceProfile';

interface IPickupOutletDelivery {
	showMap: boolean;
	pickupDelivery: boolean | undefined;
	preorderDate: string | undefined;
	selectedAddress: IAddress | undefined;
}

const PickupOutletDelivery = ({
	pickupDelivery,
	preorderDate,
	showMap,
	selectedAddress,
}: IPickupOutletDelivery): JSX.Element => {
	const { translate } = useTranslate();
	const { selectedOutlet, bookingPickupAppointment } = useOutlet();

	return (
		<Accordion
			title={translate('sim-order-details.accordion.shipping-data.title')}
			subtitle={`${translate('shipping-details.subtitle.delivery-method.title.outlet-pickup')}${'\xa0'}${
				selectedOutlet.branchName
			}`}
			subtitleColor="black54"
			pickUpAppointment={
				!!bookingPickupAppointment
					? `${translate(
							'shipping-details.accordion.appointment.outlet-appointment-description'
					  )}\xa0${moment(bookingPickupAppointment).format('D MMM YYYY, h:mm A')}`
					: ''
			}
			icon="locationPinOutlined"
			accordionHeight={104}
		>
			<AccordionContentWrapper>
				<SectionHeader>{translate('shipping-details.accordion.delivery-method.section-header')}</SectionHeader>
				<Divider marginBottom={8} />

				<DeliveryMethodWrapper paddingBottom={pickupDelivery ? 16 : undefined}>
					<Icon name="location" fill="black38" width={32} height={32} />
					<DescriptionWrapper>
						<DeliveryMethodTitle>
							{translate('shipping-details.accordion.delivery-method.title.outlet-pickup')}
						</DeliveryMethodTitle>
						<DeliveryMethodDescription>
							{translate('shipping-details.accordion.outlet.description')}
						</DeliveryMethodDescription>
						<ConditionalRender
							show={!!preorderDate && !pickupDelivery}
							onTrue={() => (
								<DeliveryMethodDescription>
									{isSoonPeorderDate(preorderDate ?? '')
										? translate('availability.preorder.subtitle.second-version')
										: translate(
												'shipping-details.accordion.delivery-method.preorder',
												preorderDate || '-'
										  )}
								</DeliveryMethodDescription>
							)}
						/>
					</DescriptionWrapper>
				</DeliveryMethodWrapper>

				<SectionHeader>{translate('shipping-details.accordion.outlet-pickup.section-header')}</SectionHeader>
				<Divider marginBottom={8} />
				<Row>
					<Col xs={12} sm={6}>
						<LabeledInformation
							label={translate('shipping-details.billing.shipping-data.data.outlet-located')}
							data={!!selectedOutlet.branchName ? selectedOutlet.branchName : ''}
						/>

						<LabeledInformation
							label={translate('shipping-details.billing.shipping-data.data.wilayat')}
							data={!!selectedOutlet.wilayat ? selectedOutlet.wilayat : ''}
						/>

						<LabeledInformation
							label={translate('shipping-details.billing.shipping-data.data.address')}
							data={!!selectedOutlet.address ? selectedOutlet.address : ''}
						/>

						<LabeledInformation
							label={translate('shipping-details.billing.shipping-data.data.phone-number')}
							data={!!selectedOutlet.phone ? selectedOutlet.phone : ''}
						/>

						<LabeledInformationWrapper>
							<Label>{translate('shipping-details.billing.shipping-data.data.opening-hours')}</Label>
							<DaysWrapper>
								<Data>
									{translate('outlet-available.modal.sunday-label')}
									{'\xa0'}
								</Data>
								<ParagraphM color={!!selectedOutlet?.openHours?.sunday ? 'black87' : 'warning'}>
									{!!selectedOutlet?.openHours?.sunday
										? selectedOutlet.openHours.sunday
										: translate('outlet-available.modal.closed-label')}
								</ParagraphM>
							</DaysWrapper>
							<DaysWrapper>
								<Data>
									{translate('outlet-available.modal.monday-label')}
									{'\xa0'}
								</Data>
								<ParagraphM color={!!selectedOutlet?.openHours?.monday ? 'black87' : 'warning'}>
									{!!selectedOutlet?.openHours?.monday
										? selectedOutlet?.openHours?.monday
										: translate('outlet-available.modal.closed-label')}
								</ParagraphM>
							</DaysWrapper>
							<DaysWrapper>
								<Data>
									{translate('outlet-available.modal.tuesday-label')}
									{'\xa0'}
								</Data>
								<ParagraphM color={!!selectedOutlet?.openHours?.tuesday ? 'black87' : 'warning'}>
									{!!selectedOutlet?.openHours?.tuesday
										? selectedOutlet?.openHours?.tuesday
										: translate('outlet-available.modal.closed-label')}
								</ParagraphM>
							</DaysWrapper>
							<DaysWrapper>
								<Data>
									{translate('outlet-available.modal.wednesday-label')}
									{'\xa0'}
								</Data>
								<ParagraphM color={!!selectedOutlet?.openHours?.wednesday ? 'black87' : 'warning'}>
									{!!selectedOutlet?.openHours?.wednesday
										? selectedOutlet?.openHours?.wednesday
										: translate('outlet-available.modal.closed-label')}
								</ParagraphM>
							</DaysWrapper>
							<DaysWrapper>
								<Data>
									{translate('outlet-available.modal.thursday-label')}
									{'\xa0'}
								</Data>
								<ParagraphM color={!!selectedOutlet?.openHours?.thursday ? 'black87' : 'warning'}>
									{!!selectedOutlet?.openHours?.thursday
										? selectedOutlet?.openHours?.thursday
										: translate('outlet-available.modal.closed-label')}
								</ParagraphM>
							</DaysWrapper>
							<DaysWrapper>
								<Data>
									{translate('outlet-available.modal.friday-label')}
									{'\xa0'}
								</Data>
								<ParagraphM color={!!selectedOutlet?.openHours?.friday ? 'black87' : 'warning'}>
									{!!selectedOutlet?.openHours?.friday
										? selectedOutlet?.openHours?.friday
										: translate('outlet-available.modal.closed-label')}
								</ParagraphM>
							</DaysWrapper>
							<DaysWrapper>
								<Data>
									{translate('outlet-available.modal.saturday-label')}
									{'\xa0'}
								</Data>
								<ParagraphM color={!!selectedOutlet?.openHours?.saturday ? 'black87' : 'warning'}>
									{!!selectedOutlet?.openHours?.saturday
										? selectedOutlet?.openHours?.saturday
										: translate('outlet-available.modal.closed-label')}
								</ParagraphM>
							</DaysWrapper>
						</LabeledInformationWrapper>
					</Col>
					<MapDetails selectedAddress={selectedAddress} showMap={showMap} />
				</Row>
				<ConditionalRender
					show={!!bookingPickupAppointment}
					onTrue={() => (
						<>
							<AppointmentSectionHeader>
								{translate('shipping-details.accordion.appointment.section-header')}
							</AppointmentSectionHeader>
							<Divider marginBottom={8} />
							<AppointmentDetailsWrapper>
								<Icon name="deliveryDate" fill="black38" width={32} height={32} />
								<DescriptionWrapper>
									<DeliveryMethodTitle>
										{translate(
											'shipping-details.accordion.delivery-method.title.outlet-pickup-appointment'
										)}
									</DeliveryMethodTitle>
									<DeliveryMethodDescription>
										{translate('shipping-details.accordion.appointment.outlet-in')}
										{'\xa0'}
										{selectedOutlet.branchName}
										{'\xa0'}
										{translate('shipping-details.accordion.appointment.outlet-on')}
										{'\xa0'}
										{bookingPickupAppointment}
									</DeliveryMethodDescription>
								</DescriptionWrapper>
							</AppointmentDetailsWrapper>
						</>
					)}
				/>
			</AccordionContentWrapper>
		</Accordion>
	);
};

export default PickupOutletDelivery;
